<template>
    <div>
        <commodity-selector
            class="inline-block flex-col w-full sm:w-auto mr-1 xs:mr-0"
            v-model="modelCommodity"
            :original="initialSymbol"
            :groupings="groups">
        </commodity-selector><select-c
            class="inline-block flex-col w-full sm:w-auto mb-1 mr-2 xs:mr-0"
            :loading="loading.optionTypes"
            v-model="modelOptionType"
            :options="optionTypes">
        </select-c><select-c
            class="inline-block flex-col w-full sm:w-auto mb-2 mr-2 xs:mr-0"
            :loading="loading.optionTypes"
            :disabled="!optionContracts || optionContracts.length === 0"
            v-model="modelOptionContract"
            :options="optionContracts">
        </select-c>
        <div class="relative inline-block flex-col w-full sm:w-auto mb-2 mr-2 xs:mr-0 clContactRadioSelector" v-if="modelOptionContract">
            <label for="calls-radio">
                <input class="inline-block self-center mr-1 mb-1" type="radio" v-model="callsOrPuts" name="calls-puts" value="calls" id="calls-radio">
                Calls
                <select-c @selected="callsOrPuts = 'calls'" v-model="modelCallSymbol" class="inline-block form-control-fixed-sm font-normal" :loading="loading.options" :options="calls"></select-c>
            </label>
            <span class="xs:block xs:mt-2 ml-0 sm:ml-2">
                <label for="puts-radio">
                    <input class="inline-block self-center mr-1 mb-1" type="radio" v-model="callsOrPuts" name="calls-puts" value="puts" id="puts-radio">
                    Puts
                    <select-c @selected="callsOrPuts = 'puts'" v-model="modelPutSymbol" class="inline-block form-control-fixed-sm font-normal" :loading="loading.options" :options="puts"></select-c>
                </label>
            </span>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useHighlightAtTheMoney } from '../../mixins/HighlightAtTheMoney';

const props = defineProps({
    // Complete details of the initial symbol
    // including category, optionType, underlying, etc.
    initialSymbol: Object,
    groups: {
        type: Object,
        required: true
    },
    modelValue: {
        type: [String, Object],
        default: null, // You can set a default value if necessary
    },
});

// Specify the emitted events
const emit = defineEmits(['update:modelValue', 'underlying', 'option']);
// const emit = defineEmits();

const { highlightAtTheMoney } = useHighlightAtTheMoney(); // Assuming you've refactored HighlightAtTheMoney into a composable.

const loading = ref({
    optionTypes: false,
    options: false
});

const modelCommodity = ref(null);
const modelOptionType = ref(null);
const modelOptionContract = ref(null);
const modelCallSymbol = ref(null);
const modelPutSymbol = ref(null);

// The response from futures-options request
// Used to pull the correct contracts for the option type
const optionTypeData = ref({});
const optionTypes = ref([]);
const optionContracts = ref([]);
const callsOrPuts = ref(props.initialSymbol?.type || 'calls');
const calls = ref([]);
const puts = ref([]);

const symbol = computed(() => {
    return callsOrPuts.value === 'calls' ? modelCallSymbol.value : modelPutSymbol.value;
});

watch(modelCommodity, newCommodity => {
    if (newCommodity && newCommodity.root) {
        getOptionTypes();
    }
});

// watch(() => modelOptionType, newOptionType => {
watch(modelOptionType, newOptionType => {
    if (newOptionType) {
        setOptionTypes();
    }
});

watch(modelOptionContract, newOptionContract => {
    if (newOptionContract) {
        getCallsAndPuts();
        emit('underlying', newOptionContract.underlyingFuture);
        emit('option', newOptionContract.symbol);
    }
});

watch(symbol, newSymbol => {
    emit('update:modelValue', newSymbol);
});

const getOptionTypes = () => {
    const route = window.route('api.futures-options');
    const params = { root: modelCommodity.value.root };

    loading.value.optionTypes = true;

    axios.post(route, params)
        .then((response) => {
            optionTypeData.value = response.data;

            // Format the object into an array, selecting the default if we have one from initialSymbol
            optionTypes.value = Object.keys(response.data).map((mOptionType) => {
                let isDefault = false;

                if (props.initialSymbol) {
                    isDefault = response.data[mOptionType].find((mOption) => {
                        return mOption.value.symbol === props.initialSymbol.contract;
                    });
                }

                return {
                    label: mOptionType,
                    value: mOptionType,
                    default: isDefault
                };
            });

            const defaultItem = optionTypes.value.find((mOptionType) => mOptionType.default);
            modelOptionType.value = defaultItem ? defaultItem.label : optionTypes.value[0].label;

            setOptionTypes();
        })
        .catch(error => { /* handle error */ })
        .then(() => {
            loading.value.optionTypes = false;
        });
};

const setOptionTypes = () => {
    // Set the default contract
    if (props.initialSymbol) {
        optionContracts.value = optionTypeData.value[modelOptionType.value].map((mOption) => {
            const isDefault = mOption.value.symbol === props.initialSymbol.contract;

            if (isDefault) {
                mOption.default = true;
            }

            return mOption;
        });
    } else {
        optionContracts.value = optionTypeData.value[modelOptionType.value];
    }

    const defaultItem = optionContracts.value.find((mOption) => mOption.default);
    modelOptionContract.value = defaultItem ? defaultItem.value : optionContracts.value[0].value;
};

const getUnderlyingLastPrice = () => {
    const route = window.route('api.quotes');

    const params = {
        symbol: modelOptionContract.value.underlyingFuture,
        fields: 'lastPrice',
        raw: true
    };

    return axios.post(route, params)
        .then((response) => {
            return response.data.data[0].raw.lastPrice;
        })
        .catch(error => { /* handle error */ });
};

const getCallsAndPuts = () => {
    const route = window.route('api.quotes');

    const params = {
        list: 'futures.options',
        symbol: modelOptionContract.value.symbol,
        fields: 'shortSymbol,longSymbol,optionType,strikePrice',
        groupBy: 'optionType',
        raw: true
    };

    const optionFormatter = (data) => {
        return data.map((mOption) => {
            // TODO highlight at the money

            let isDefault = false;

            if (props.initialSymbol) {
                isDefault = props.initialSymbol.symbol === mOption.longSymbol;
            }

            return {
                label: mOption.strikePrice,
                value: mOption.longSymbol,
                highlight: mOption.highlight || false,
                default: isDefault
            };
        })
    };

    loading.value.options = true;

    const makeRequest = () => {
        return axios.post(route, params)
            .then((response) => {
                return {
                    calls: response.data.data.Call,
                    puts: response.data.data.Put
                };
            })
            .catch(error => { /* handle error */ });
    };

    Promise.all([
        getUnderlyingLastPrice(),
        makeRequest()
    ])
    .then((args) => {
        const [lastPrice, callsPuts] = args;
        loading.value.options = false;
        calls.value = optionFormatter(highlightAtTheMoney(lastPrice, callsPuts.calls.reverse())).reverse();
        puts.value = optionFormatter(highlightAtTheMoney(lastPrice, callsPuts.puts.reverse())).reverse();

        // set default
        const defaultCall = calls.value.find((mDefault) => mDefault.default);
        modelCallSymbol.value = defaultCall ? defaultCall.value : calls.value[0].value;

        const defaultPut = puts.value.find((mDefault) => mDefault.default);
        modelPutSymbol.value = defaultPut ? defaultPut.value : puts.value[0].value;
    })
    .catch((error) => { /* handle error */ });
};

</script>
